import React from "react";
import {
  LoginPageProps,
  LoginFormTypes,
  useLink,
  useRouterType,
  useActiveAuthProvider,
  useLogin,
  useTranslate,
  useRouterContext,
} from "@refinedev/core";
import {
  Form,
  Input,
  Button,
  Checkbox,
  CardProps,
  LayoutProps,
  FormProps,
  Divider,
  Typography,
} from "antd";
import styles from "./login.module.css";

const { Text, Title } = Typography;

type LoginProps = LoginPageProps<LayoutProps, CardProps, FormProps>;

export const LoginPage: React.FC<LoginProps> = ({
  providers,
  registerLink,
  forgotPasswordLink,
  rememberMe,
  contentProps,
  wrapperProps,
  renderContent,
  formProps,
  title,
}) => {
  const [form] = Form.useForm<LoginFormTypes>();
  const t = useTranslate();
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();

  const ActiveLink = routerType === "legacy" ? LegacyLink : Link;

  const authProvider = useActiveAuthProvider();
  const { mutate: login, isLoading } = useLogin<LoginFormTypes>({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });

  const renderProviders = () => {
    if (providers && providers.length > 0) {
      return (
        <>
          {providers.map((provider) => {
            return (
              <button
                key={provider.name}
                type="button"
                onClick={() =>
                  login({
                    providerName: provider.name,
                  })
                }
                className={styles.googleButton}
              >
                <span className={styles.googleButtonContent}>
                  {provider.icon}
                  <span>{provider.label}</span>
                </span>
              </button>
            );
          })}
          <Divider>
            <Text style={{ color: "#6b7280", fontSize: "14px" }}>
              {t("pages.login.divider", "ou")}
            </Text>
          </Divider>
        </>
      );
    }
    return null;
  };

  const CardContent = (
    <div className={styles.cardContainer}>
      {/* Header */}
      <div className={styles.cardHeader}>
        {title !== false && (
          <div className={styles.logoContainer}>
            <img
              src="/images/logo.png"
              alt="Split Fácil"
              className={styles.logoIcon}
              style={{ height: "2.25rem", width: "auto", maxWidth: "200px" }}
            />
          </div>
        )}
        <Title level={2} className={styles.cardTitle}>
          Bem-vindo de volta
        </Title>
        <Text className={styles.cardSubtitle}>
          Entre na sua conta para gerenciar suas cobranças
        </Text>
      </div>

      {/* Body */}
      <div className={styles.cardBody}>
      {renderProviders()}
        
      <Form<LoginFormTypes>
        layout="vertical"
        form={form}
        onFinish={(values) => login(values)}
        requiredMark={false}
        initialValues={{
          remember: false,
        }}
        {...formProps}
      >
        <Form.Item
          name="email"
            label={
              <span className={styles.textLabel}>
                {t("pages.login.fields.email", "Email")}
              </span>
            }
          rules={[
            { required: true },
            {
              type: "email",
              message: t(
                "pages.login.errors.validEmail",
                  "Email inválido"
              ),
            },
          ]}
        >
          <Input
            size="large"
            placeholder={t("pages.login.fields.email", "Email")}
              className={styles.inputCustom}
          />
        </Form.Item>

        <Form.Item
          name="password"
            label={
              <span className={styles.textLabel}>Senha</span>
            }
          rules={[{ required: true }]}
        >
            <Input
              type="password"
              placeholder="●●●●●●●●"
              size="large"
              className={styles.inputCustom}
            />
        </Form.Item>

          <div className={`${styles.mb6} ${styles.flexBetween}`}>
            {rememberMe && (
            <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox className={styles.textSecondary}>
                  {t("pages.login.buttons.rememberMe", "Lembrar-me")}
              </Checkbox>
            </Form.Item>
          )}
            {forgotPasswordLink && (
            <ActiveLink
              to="/forgot-password"
                className={styles.linkStyle}
            >
              Esqueceu a senha?
            </ActiveLink>
          )}
        </div>

        <Form.Item>
            <button
              type="submit"
              disabled={isLoading}
              className={styles.primaryButton}
            >
              <span className={styles.primaryButtonContent}>
                {isLoading ? "Entrando..." : "Entrar"}
              </span>
              <div className={styles.primaryButtonOverlay}></div>
            </button>
        </Form.Item>
      </Form>

        {registerLink && (
          <div className={`${styles.mt6} ${styles.textCenter}`}>
            <Text className={styles.textSecondary}>
              Não tem uma conta?{" "}
            <ActiveLink
              to="/register"
                className={styles.linkBold}
            >
              Cadastre-se
            </ActiveLink>
          </Text>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={styles.loginContainer}
      style={wrapperProps?.style}
      data-theme="light"
    >
      {/* Painel de marca (esquerda) */}
      <div className={styles.brandPanel}>
        <div className={styles.brandGlow} />
        <div className={styles.brandContent}>
          <img
            src="/images/logo.png"
            alt="Split Fácil"
            className={styles.brandLogo}
          />
          <h1 className={styles.brandHeadline}>
            Cobranças com split automático, do jeito simples.
          </h1>
          <p className={styles.brandText}>
            Receba pagamentos por PIX e boleto e divida o valor entre os
            recebedores automaticamente - sem planilhas, sem dor de cabeça.
          </p>
          <ul className={styles.brandFeatures}>
            <li><span className={styles.brandCheck}>✓</span> Split automático entre recebedores</li>
            <li><span className={styles.brandCheck}>✓</span> PIX e boleto em segundos</li>
            <li><span className={styles.brandCheck}>✓</span> Histórico e repasses organizados</li>
          </ul>
        </div>
      </div>

      {/* Formulário (direita) */}
      <div className={styles.formPanel}>
        {renderContent ? (
          renderContent(CardContent, null)
        ) : (
          <div className={styles.fadeUp}>{CardContent}</div>
        )}
      </div>
    </div>
  );
};
